import makeStyles from '@mui/styles/makeStyles';
import AgyleTextField from './agyleTextField';
import {useDispatch, useSelector} from 'react-redux';
import {agylePref, saveAgylePreference} from '../../../store/reducers/preferences';
import React, {useEffect, useRef, useState} from 'react';
import _ from 'lodash';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import {useTranslation} from 'react-i18next';

const useStyles = makeStyles(theme => ({
    container: {
        position: 'relative',
        width: '100%',
    },
    historyContainer: {
        position: 'absolute',
        top: 'calc(100% + 5px)',
        left: 0,
        width: '100%',
        backgroundColor: theme.palette.mode === 'dark' ? '#313233' : '#DEE1E5',
        border: '1px solid #909194',
        borderRadius: 8,
        zIndex: 69,
        padding: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
    },
    historyItem: {
        backgroundColor: theme.palette.mode === 'dark' ? '#3D3E40' : '#FFFFFF',
        padding: '2px 10px',
        borderRadius: 8,
        cursor: 'pointer',
        width: '100%',
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        '&:hover': {
            backgroundColor: theme.palette.mode === 'light' ? '#EFF1F3' : '#4b4b4b',
        },
    },
    clear: {
        color: theme.palette.primary.main,
        fontSize: 'small',
        cursor: 'pointer',
        '&:hover': {
            color: theme.palette.primary.dark,
        },
    },
}));

const AgyleTextFieldWithHistory = ({enableSearch, path, callBack, value, onKeyDown, ...props}) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const agylePreference = useSelector(agylePref);
    const {t} = useTranslation();

    const historyRef = useRef(null);
    const textFieldRef = useRef(null);

    // History state
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);

    // Value
    const [innerValue, setInnerValue] = useState(value);

    // Close history
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!textFieldRef?.current?.contains(event.target) && !historyRef.current?.contains(event.target)) {
                setShowHistory(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Load history search from user preference
    useEffect(() => {
        if (enableSearch && path && _.isArray(agylePreference?.searchHistory?.[path])) {
            setHistory(agylePreference?.searchHistory?.[path] || []);
            setHistoryLoaded(true);
        }
    }, [enableSearch]);

    useEffect(() => {
        setInnerValue(value);
    }, [value]);

    useEffect(() => {
        if (historyLoaded && !_.isEqual(history, agylePreference?.searchHistory?.[path])) {
            const obj = {
                searchHistory: {
                    ...agylePreference.searchHistory,
                    [path]: [...history],
                },
            };
            dispatch(saveAgylePreference(obj));
        }
    }, [history, historyLoaded]);

    const handleFocus = () => {
        setShowHistory(true);
    };

    const handleDeleteItem = (item) => {
        const clone = _.cloneDeep(history);
        const newHist = _.filter(clone, opt => opt !== item);
        setHistory(newHist);
    };

    const handleSelectItem = (e, item) => {
        const deleteButtonSelected = e.target.closest('.disabled-options') !== null;
        if (!deleteButtonSelected) {
            setInnerValue(item);
            setShowHistory(false);

            // Modify history
            const clone = _.filter(history, ref => ref !== item);
            clone.unshift(item);
            setHistory(clone);

            if (callBack) callBack(item);
        }
    };

    const handleAddHistory = () => {
        if (!_.isEmpty(innerValue)) {
            let clone = _.cloneDeep(history);
            const index = clone.indexOf(innerValue);
            if (index === -1) {
                clone.unshift(innerValue);
                if (clone.length > 5) {
                    clone = clone.slice(0, 5);
                }
                setHistory(clone);
            }
        }
    };

    const handleOnKeyDown = (e) => {
        if (onKeyDown) onKeyDown(e);
        if (e?.key === 'Enter') {
            setShowHistory(false);
            handleAddHistory();
        }
    };

    return (
        <div className={classes.container}>
            <AgyleTextField
                ref={textFieldRef}
                autoComplete="off"
                onFocus={handleFocus}
                value={innerValue}
                onKeyDown={handleOnKeyDown}
                endAdornment={<span className={classes.clear} onClick={() => setInnerValue('')}>{t('Clear')}</span>}
                {...props}
            />

            {showHistory && history.length > 0 &&
                <div ref={historyRef} className={classes.historyContainer}>
                    {history.map((item, index) => (
                        <div
                            key={index}
                            className={classes.historyItem}
                            onClick={(e) => handleSelectItem(e, item)}
                        >
                            {item}
                            <IconButton
                                edge="end"
                                aria-label="delete"
                                className={'disabled-options'}
                                onClick={(e) => handleDeleteItem(item)}
                            >
                                <DeleteIcon/>
                            </IconButton>
                        </div>
                    ))}
                </div>
            }
        </div>
    );
};

export default AgyleTextFieldWithHistory;